import { useCallback, useEffect, useRef } from 'react';
import type { Message } from '../types/message';

const isElectron = typeof window !== 'undefined' && !!(window as any).electronAPI?.isElectron;

interface AgentLoopParams {
  messages: any[];
  newMessages: any[];
  tools: any[] | null;
  model: { id: number; name: string; model_family: string };
  settings: { thinking_mode: string | null };
  apiKeys: Record<string, string | null>;
  systemPrompt: string;
  conversationId: string | null;
  webSearchEnabled: boolean;
  cwd: string;
  accessToken: string;
}

interface LoopUIContext {
  setMessages: (updater: React.SetStateAction<Message[]>) => void;
  messageId: string;
}

interface LoopState extends LoopUIContext {
  resolve: (loopId: string) => void;
  reject: (err: Error) => void;
  legIndex: number;
  inSubAgent: boolean;
}

/**
 * Hook that bridges the Electron agent loop IPC events to the React UI.
 * Supports multiple concurrent loops keyed by loopId, so streaming in
 * one tab does not interfere with another.
 *
 * Returns null when running in the browser (non-Electron).
 */
export function useAgentLoop() {
  const loopsRef = useRef<Map<string, LoopState>>(new Map());

  // Register one IPC listener on mount, dispatch by loopId
  useEffect(() => {
    if (!isElectron) return;

    const electronAPI = (window as any).electronAPI;

    // Bridge main-process network logs to the renderer DevTools console
    electronAPI.onMainProcessLog?.((log: any) => {
      const { source, kind, ...rest } = log;
      const prefix = `[Main/${source}]`;
      if (kind === 'error') {
        console.error(prefix, rest);
      } else {
        console.log(prefix, kind, rest);
      }
    });

    electronAPI.agentLoop.onEvent((event: any) => {
      const loop = loopsRef.current.get(event.loopId);
      if (!loop) return;

      try {
        processEvent(event, loop, loopsRef.current);
      } catch (err) {
        console.error('Error processing agent loop event:', err);
      }
    });

    return () => {
      electronAPI.agentLoop.removeListener();
      electronAPI.removeMainProcessLogListener?.();
    };
  }, []);

  /**
   * Start the agent loop. Returns the loopId immediately for abort support,
   * plus a completion Promise that resolves when the loop finishes.
   */
  const startLoop = useCallback(async (params: AgentLoopParams, ui: LoopUIContext): Promise<{ loopId: string; completion: Promise<string> }> => {
    if (!isElectron) throw new Error('Not in Electron');

    const electronAPI = (window as any).electronAPI;

    // Start the loop first to get the loopId
    const { loopId } = await electronAPI.agentLoop.start(params);

    // Create a completion Promise that resolves when done/error events arrive
    const completion = new Promise<string>((resolve, reject) => {
      loopsRef.current.set(loopId, {
        ...ui,
        resolve,
        reject,
        legIndex: 1,
        inSubAgent: false,
      });
    });

    return { loopId, completion };
  }, []);

  const abortLoop = useCallback((loopId?: string) => {
    if (!isElectron) return;
    const electronAPI = (window as any).electronAPI;
    const abortError = new Error('AbortError');
    abortError.name = 'AbortError';

    if (loopId) {
      const loop = loopsRef.current.get(loopId);
      electronAPI.agentLoop.abort(loopId);
      loopsRef.current.delete(loopId);
      loop?.reject(abortError);
    } else {
      // Abort all loops (fallback)
      const loops = Array.from(loopsRef.current.entries());
      loopsRef.current.clear();
      loops.forEach(([id, loop]) => {
        electronAPI.agentLoop.abort(id);
        loop.reject(abortError);
      });
    }
  }, []);

  if (!isElectron) return null;

  return { startLoop, abortLoop, loopsRef };
}

// ===== Event processing (operates on per-loop state) =====

function updateMessage(loop: LoopState, updater: (parts: any[]) => any[]) {
  loop.setMessages((prevMessages: Message[]) => {
    const msgIndex = prevMessages.findIndex(m => m.id === loop.messageId);
    if (msgIndex === -1) return prevMessages;

    const newMessages = prevMessages.slice();
    const prevMsg = prevMessages[msgIndex] as any;
    newMessages[msgIndex] = {
      ...prevMsg,
      message_parts: updater(prevMsg.message_parts || []),
    } as Message;
    return newMessages;
  });
}

function processEvent(event: any, loop: LoopState, loops: Map<string, LoopState>): void {
  switch (event.type) {
    case 'thinking_start':
      updateMessage(loop, (parts) => [
        ...parts,
        {
          type: 'thinking',
          content: '',
          duration_ms: null,
          metadata: { ...(event.data?.metadata || {}), leg_index: loop.legIndex },
        },
      ]);
      break;

    case 'thinking': {
      const chunk = event.data?.chunk || '';
      updateMessage(loop, (parts) => {
        const idx = findLastPartIndex(parts, 'thinking', loop.legIndex);
        if (idx === -1) return parts;
        const updated = [...parts];
        updated[idx] = { ...updated[idx], content: updated[idx].content + chunk };
        return updated;
      });
      break;
    }

    case 'thinking_end':
      updateMessage(loop, (parts) => {
        const idx = findLastPartIndex(parts, 'thinking', loop.legIndex);
        if (idx === -1) return parts;
        const updated = [...parts];
        updated[idx] = {
          ...updated[idx],
          content: event.data?.content || updated[idx].content,
          duration_ms: event.data?.duration_ms || null,
        };
        return updated;
      });
      break;

    case 'answer_start':
      updateMessage(loop, (parts) => [
        ...parts,
        {
          type: 'answer',
          content: '',
          metadata: { ...(event.data?.metadata || {}), leg_index: loop.legIndex },
        },
      ]);
      break;

    case 'answer': {
      const chunk = event.data?.chunk || '';
      updateMessage(loop, (parts) => {
        const idx = findLastPartIndex(parts, 'answer', loop.legIndex);
        if (idx === -1) return parts;
        const updated = [...parts];
        updated[idx] = { ...updated[idx], content: updated[idx].content + chunk };
        return updated;
      });
      break;
    }

    case 'answer_end':
      updateMessage(loop, (parts) => {
        const idx = findLastPartIndex(parts, 'answer', loop.legIndex);
        if (idx === -1) return parts;
        const updated = [...parts];
        updated[idx] = {
          ...updated[idx],
          content: event.data?.content || updated[idx].content,
        };
        return updated;
      });
      break;

    case 'tool_use_start':
      if (loop.inSubAgent) break;
      updateMessage(loop, (parts) => {
        const alreadyExists = parts.some(
          (p: any) => p.type === 'tool_use' && p.tool_use_id === event.data?.tool_use_id
        );
        if (alreadyExists) return parts;
        return [
          ...parts,
          {
            type: 'tool_use',
            tool_use_id: event.data?.tool_use_id,
            name: event.data?.name,
            tool_type: event.data?.type || 'custom',
            parameters: null,
            metadata: { ...(event.data?.metadata || {}), leg_index: loop.legIndex },
          },
        ];
      });
      break;

    case 'tool_use':
      break;

    case 'tool_use_end':
      updateMessage(loop, (parts) => {
        const idx = parts.findIndex(
          (p: any) => p.type === 'tool_use' && p.tool_use_id === event.data?.tool_use_id
        );
        if (idx === -1) return parts;
        const updated = [...parts];
        updated[idx] = {
          ...updated[idx],
          parameters: event.data?.parameters || null,
        };
        return updated;
      });
      break;

    case 'tool_use_result':
      updateMessage(loop, (parts) => {
        const alreadyExists = parts.some(
          (p: any) => p.type === 'tool_result' && p.tool_use_id === event.data?.tool_use_id
        );
        if (alreadyExists) return parts;
        return [
          ...parts,
          {
            type: 'tool_result',
            tool_use_id: event.data?.tool_use_id,
            name: event.data?.name,
            result: event.data?.result || '',
            metadata: { leg_index: loop.legIndex },
          },
        ];
      });
      break;

    case 'tool_output':
      updateMessage(loop, (parts) => {
        const existingIdx = parts.findIndex(
          (p: any) => p.type === 'tool_result' && p.tool_use_id === event.data?.tool_use_id
        );
        if (existingIdx !== -1) {
          const updated = [...parts];
          updated[existingIdx] = {
            ...updated[existingIdx],
            result: (updated[existingIdx].result || '') + (event.data?.chunk || ''),
          };
          return updated;
        }
        return [
          ...parts,
          {
            type: 'tool_result',
            tool_use_id: event.data?.tool_use_id,
            name: event.data?.name || '',
            result: event.data?.chunk || '',
            metadata: { leg_index: loop.legIndex },
          },
        ];
      });
      break;

    case 'leg_start':
      loop.legIndex = event.data?.leg_index || 1;
      break;

    case 'leg_end':
      break;

    case 'sub_agent_start': {
      loop.inSubAgent = true;
      const subAgentId = event.data?.tool_use_id;
      updateMessage(loop, (parts) => {
        const idx = parts.findIndex(
          (p: any) => p.type === 'tool_use' && p.tool_use_id === subAgentId
        );
        if (idx === -1) return parts;
        const updated = [...parts];
        updated[idx] = {
          ...updated[idx],
          sub_agent_model: event.data?.model || null,
          streaming_content: '',
        };
        return updated;
      });
      break;
    }

    case 'sub_agent_progress': {
      const chunk = event.data?.chunk || '';
      const progressId = event.data?.tool_use_id;
      updateMessage(loop, (parts) => {
        const idx = parts.findIndex(
          (p: any) => p.type === 'tool_use' && p.tool_use_id === progressId
        );
        if (idx === -1) return parts;
        const updated = [...parts];
        updated[idx] = {
          ...updated[idx],
          streaming_content: (updated[idx].streaming_content || '') + chunk,
        };
        return updated;
      });
      break;
    }

    case 'sub_agent_tool_start': {
      const toolStartId = event.data?.tool_use_id;
      const toolName = event.data?.name || '';
      const toolParams = event.data?.parameters
        ? (typeof event.data.parameters === 'string'
          ? (() => { try { return JSON.parse(event.data.parameters); } catch { return null; } })()
          : event.data.parameters)
        : null;
      updateMessage(loop, (parts) => {
        const idx = parts.findIndex(
          (p: any) => p.type === 'tool_use' && p.tool_use_id === toolStartId
        );
        if (idx === -1) return parts;
        const updated = [...parts];
        const prev = updated[idx] as any;
        const activity = [...(prev.streaming_activity || []), { type: 'tool', name: toolName, parameters: toolParams, status: 'active' }];
        updated[idx] = { ...prev, streaming_activity: activity };
        return updated;
      });
      break;
    }

    case 'sub_agent_tool_update': {
      const toolUpdateId = event.data?.tool_use_id;
      const updateParams = event.data?.parameters
        ? (typeof event.data.parameters === 'string'
          ? (() => { try { return JSON.parse(event.data.parameters); } catch { return null; } })()
          : event.data.parameters)
        : null;
      if (updateParams) {
        updateMessage(loop, (parts) => {
          const idx = parts.findIndex(
            (p: any) => p.type === 'tool_use' && p.tool_use_id === toolUpdateId
          );
          if (idx === -1) return parts;
          const updated = [...parts];
          const prev = updated[idx] as any;
          const activity = (prev.streaming_activity || []).map((item: any, i: number, arr: any[]) => {
            if (item.type === 'tool' && item.status === 'active' && i === arr.findLastIndex((a: any) => a.type === 'tool' && a.status === 'active')) {
              return { ...item, parameters: updateParams };
            }
            return item;
          });
          updated[idx] = { ...prev, streaming_activity: activity };
          return updated;
        });
      }
      break;
    }

    case 'sub_agent_tool_end': {
      const toolEndId = event.data?.tool_use_id;
      updateMessage(loop, (parts) => {
        const idx = parts.findIndex(
          (p: any) => p.type === 'tool_use' && p.tool_use_id === toolEndId
        );
        if (idx === -1) return parts;
        const updated = [...parts];
        const prev = updated[idx] as any;
        const activity = (prev.streaming_activity || []).map((item: any, i: number, arr: any[]) => {
          if (item.type === 'tool' && item.status === 'active' && i === arr.findLastIndex((a: any) => a.type === 'tool' && a.status === 'active')) {
            return { ...item, status: 'done' };
          }
          return item;
        });
        updated[idx] = { ...prev, streaming_activity: activity };
        return updated;
      });
      break;
    }

    case 'sub_agent_thinking_start': {
      const thinkStartId = event.data?.tool_use_id;
      updateMessage(loop, (parts) => {
        const idx = parts.findIndex(
          (p: any) => p.type === 'tool_use' && p.tool_use_id === thinkStartId
        );
        if (idx === -1) return parts;
        const updated = [...parts];
        const prev = updated[idx] as any;
        const activity = [...(prev.streaming_activity || []), { type: 'thinking', content: '', status: 'active' }];
        updated[idx] = { ...prev, streaming_activity: activity };
        return updated;
      });
      break;
    }

    case 'sub_agent_thinking': {
      const thinkingChunk = event.data?.chunk || '';
      const thinkId = event.data?.tool_use_id;
      updateMessage(loop, (parts) => {
        const idx = parts.findIndex(
          (p: any) => p.type === 'tool_use' && p.tool_use_id === thinkId
        );
        if (idx === -1) return parts;
        const updated = [...parts];
        const prev = updated[idx] as any;
        const activity = (prev.streaming_activity || []).map((item: any, i: number, arr: any[]) => {
          if (item.type === 'thinking' && item.status === 'active' && i === arr.findLastIndex((a: any) => a.type === 'thinking' && a.status === 'active')) {
            return { ...item, content: item.content + thinkingChunk };
          }
          return item;
        });
        updated[idx] = { ...prev, streaming_activity: activity };
        return updated;
      });
      break;
    }

    case 'sub_agent_thinking_end': {
      const thinkEndId = event.data?.tool_use_id;
      updateMessage(loop, (parts) => {
        const idx = parts.findIndex(
          (p: any) => p.type === 'tool_use' && p.tool_use_id === thinkEndId
        );
        if (idx === -1) return parts;
        const updated = [...parts];
        const prev = updated[idx] as any;
        const activity = (prev.streaming_activity || []).map((item: any, i: number, arr: any[]) => {
          if (item.type === 'thinking' && item.status === 'active' && i === arr.findLastIndex((a: any) => a.type === 'thinking' && a.status === 'active')) {
            return { ...item, status: 'done', duration_ms: event.data?.duration_ms || null };
          }
          return item;
        });
        updated[idx] = {
          ...prev,
          streaming_activity: activity,
        };
        return updated;
      });
      break;
    }

    case 'sub_agent_end': {
      loop.inSubAgent = false;
      const endToolUseId = event.data?.tool_use_id;
      updateMessage(loop, (parts) => {
        const alreadyExists = parts.some(
          (p: any) => p.type === 'tool_result' && p.tool_use_id === endToolUseId
        );
        if (alreadyExists) return parts;

        const updated = parts.map((p: any) =>
          p.type === 'tool_use' && p.tool_use_id === endToolUseId
            ? { ...p, streaming_content: undefined }
            : p
        );

        return [
          ...updated,
          {
            type: 'tool_result',
            tool_use_id: endToolUseId,
            name: event.data?.name,
            result: event.data?.result || '',
            trace: event.data?.trace || [],
            metadata: { leg_index: loop.legIndex },
          },
        ];
      });
      break;
    }

    case 'metadata':
      if (event.data?.type === 'message_end' && event.data?.usage) {
        loop.setMessages((prevMessages: Message[]) => {
          const msgIndex = prevMessages.findIndex(m => m.id === loop.messageId);
          if (msgIndex === -1) return prevMessages;
          const newMessages = prevMessages.slice();
          const prevMsg = prevMessages[msgIndex] as any;
          newMessages[msgIndex] = {
            ...prevMsg,
            metadata: {
              ...(prevMsg.metadata || {}),
              usage_log: event.data.usage,
            },
          } as Message;
          return newMessages;
        });
      }
      break;

    case 'done': {
      const loopId = event.loopId;
      const { resolve } = loop;
      loops.delete(loopId);
      resolve(loopId);
      break;
    }

    case 'error': {
      const loopId = event.loopId;
      const { reject } = loop;
      loops.delete(loopId);
      reject(new Error(event.message || 'Agent loop error'));
      break;
    }
  }
}

function findLastPartIndex(parts: any[], type: string, legIndex: number): number {
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i].type === type && parts[i].metadata?.leg_index === legIndex) {
      return i;
    }
  }
  return -1;
}
